import { useState, type FormEvent, type ReactNode } from "react"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { AuthField } from "../AuthField/AuthField"
import { Input } from "../Input/Input"
import { PasswordInput } from "../PasswordInput/PasswordInput"
import { Button } from "../Button/Button"

export interface SignInFormErrors {
  email?: string
  password?: string
}

export interface SignInFormValues {
  email: string
  password: string
}

export interface SignInFormProps {
  heading?: string
  description?: string
  defaultEmail?: string
  defaultPassword?: string
  loading?: boolean
  errors?: SignInFormErrors
  submitLabel?: string
  submittingLabel?: string
  emailLabel?: string
  passwordLabel?: string
  forgotLabel?: string
  signUpPrompt?: ReactNode
  signUpLinkSlot?: ReactNode
  forgotSlot?: ReactNode
  captchaSlot?: ReactNode
  onSubmit?: (values: SignInFormValues) => void
  className?: string
}

export function SignInForm({
  heading = "Welcome back",
  description,
  defaultEmail = "",
  defaultPassword = "",
  loading = false,
  errors,
  submitLabel = "Login",
  submittingLabel = "Signing in...",
  emailLabel = "Email",
  passwordLabel = "Password",
  forgotLabel = "Forgot?",
  signUpPrompt,
  signUpLinkSlot,
  forgotSlot,
  captchaSlot,
  onSubmit,
  className,
}: SignInFormProps) {
  const [email, setEmail] = useState(defaultEmail)
  const [password, setPassword] = useState(defaultPassword)

  const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    onSubmit?.({ email, password })
  }

  const forgotLink = forgotSlot ?? (
    <span className="text-caption text-muted-foreground">{forgotLabel}</span>
  )

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="text-center mb-5">
        <h1 className="text-page-title text-foreground mb-1">{heading}</h1>
        {description && <p className="text-body text-muted-foreground">{description}</p>}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
        <AuthField label={emailLabel} error={errors?.email}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="h-9 text-body bg-background border-border"
          />
        </AuthField>

        <AuthField label={passwordLabel} error={errors?.password} action={forgotLink}>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            autoComplete="current-password"
            className="h-9 text-body bg-background border-border"
          />
        </AuthField>

        {captchaSlot}

        <Button type="submit" disabled={loading} className="w-full text-label">
          {loading && <Loader2 size={14} className="animate-spin mr-1.5" />}
          {loading ? submittingLabel : submitLabel}
        </Button>
      </form>

      {(signUpPrompt || signUpLinkSlot) && (
        <p className="text-center text-body text-muted-foreground mt-5">
          {signUpPrompt} {signUpLinkSlot}
        </p>
      )}
    </div>
  )
}
